/**
 * @hector/piece-number-sort
 *
 * Tri « naturel » canonique des numéros de pièces juridiques françaises,
 * partagé backend + legalworkspace (source de vérité unique — DEBT de la
 * dérive à 4 stratégies, cf. docs/features/piece-ingestion/plans/
 * 2026-07-02-nomenclature-tri-folders-bordereau-renumber.html §Feature 4).
 *
 * Gère : entier de tête, suffixes FR (bis/ter/…), sous-numéro, annexes.
 *
 * Décision clé (2026-07-02) : le parseur est **tolérant à tous les
 * séparateurs** (`.` `-` `_` `/` `)` `:`) et **non ancré en fin** — un
 * libellé comme `"2. Objet du document"` se trie sur son entier de tête `2`,
 * là où l'ancien regex backend (ancré `$`, séparateurs `[-./]` seuls) le
 * reléguait en `other` → tri lexical faux. Voir tests/pieceNumberSort.test.ts.
 *
 * @module @hector/piece-number-sort
 */

import { ROMAN_PIECE_RE, romanToInt } from './romanSections';

/** Suffixes juridiques FR, dans l'ordre de tri. */
const SUFFIX_ORDER: Record<string, number> = {
  '': 0,
  bis: 1,
  ter: 2,
  quater: 3,
  quinquies: 4,
  sexies: 5,
  septies: 6,
  octies: 7,
  nonies: 8,
  decies: 9,
};

/**
 * Ordre des familles : numérique < romain < annexe < autre (texte).
 *
 * `roman` s'intercale après `numeric` : dans un dossier en cours de migration
 * (cas réel `II - MEDCAL`, qui porte à la fois `2-1` et `II-3`), les pièces
 * encore numérotées à l'arabe précèdent les pièces déjà sectionnées.
 * Valeurs internes uniquement — aucun consommateur externe ne les lit.
 */
const TYPE_ORDER: Record<ParsedPiece['type'], number> = {
  numeric: 0,
  roman: 1,
  annexe: 2,
  other: 3,
};

/**
 * Entier de tête + suffixe FR optionnel + sous-numéro optionnel après
 * N'IMPORTE quel séparateur (`. - _ / ) :`), avec espaces tolérés autour.
 * Non ancré : tout texte de titre après le numéro est ignoré.
 *
 * Le sous-numéro « lettre » n'est capté que s'il est isolé (`(?![A-Za-z])`)
 * pour ne pas avaler la première lettre d'un mot (`"2. Objet"` ≠ sub `O`).
 */
const NUMERIC_RE =
  /^\s*(\d+)\s*(bis|ter|quater|quinquies|sexies|septies|octies|nonies|decies)?(?:\s*[-._/):]\s*(\d+|[A-Za-z](?![A-Za-z])))?/i;

/** « Annexe A », « ANNEXE 1 », « annexe b »… (lettre ou nombre). */
const ANNEXE_RE = /^\s*annexe\s*([A-Za-z]|\d+)\b/i;

/** Représentation structurée triable d'un numéro de pièce. */
export interface ParsedPiece {
  type: 'numeric' | 'roman' | 'annexe' | 'other';
  /** numeric : entier de tête */
  base?: number;
  /** numeric : suffixe FR normalisé en minuscules ('' si aucun) */
  suffix?: string;
  /** numeric/roman : sous-numéro (0 si aucun ; lettre → A=1, B=2…) */
  sub?: number;
  /** roman/annexe/other : valeur normalisée (roman : le jeton, ex. `'IV'`) */
  value?: string;
  /** roman : valeur entière du jeton romain (`'IV'` → 4) */
  romanValue?: number;
  /** annexe : valeur numérique de tri (A=1… ou le nombre) */
  numericValue?: number;
  /** chaîne d'origine trimée */
  raw: string;
}

/** Convertit un sous-numéro capté (chiffre ou lettre isolée) en entier. */
function subToNumber(subRaw: string): number {
  if (/^\d+$/.test(subRaw)) return parseInt(subRaw, 10);
  return subRaw.toUpperCase().charCodeAt(0) - 64; // A=1, B=2, …
}

/**
 * Parse un numéro de pièce en composants triables.
 *
 * @example
 * parsePieceNumber('2.')      // { type:'numeric', base:2, suffix:'', sub:0 }
 * parsePieceNumber('2_1')     // { type:'numeric', base:2, sub:1 }
 * parsePieceNumber('2bis')    // { type:'numeric', base:2, suffix:'bis' }
 * parsePieceNumber('2. Objet')// { type:'numeric', base:2, sub:0 }
 * parsePieceNumber('Annexe A')// { type:'annexe', numericValue:1 }
 */
export function parsePieceNumber(input: unknown): ParsedPiece {
  if (input === null || input === undefined || typeof input !== 'string') {
    const raw = String(input ?? '');
    return { type: 'other', value: raw.toLowerCase(), raw };
  }

  const trimmed = input.trim();
  if (trimmed === '') return { type: 'other', value: '', raw: trimmed };

  const numeric = NUMERIC_RE.exec(trimmed);
  if (numeric && numeric[1]) {
    const subRaw = numeric[3];
    return {
      type: 'numeric',
      base: parseInt(numeric[1], 10),
      suffix: (numeric[2] ?? '').toLowerCase(),
      sub: subRaw ? subToNumber(subRaw) : 0,
      raw: trimmed,
    };
  }

  // Section romaine (`I-1`, `II.3`). Testé APRÈS le numérique — un `2-1` reste
  // arabe — et seulement si le jeton est un romain CANONIQUE de l'alphabet
  // `[IVX]` : `IIII-1` ou `D-1` retombent volontairement en `other`.
  const roman = ROMAN_PIECE_RE.exec(trimmed);
  if (roman && roman[1] && roman[2]) {
    const romanValue = romanToInt(roman[1]);
    if (romanValue !== null) {
      return {
        type: 'roman',
        romanValue,
        value: roman[1].toUpperCase(),
        sub: parseInt(roman[2], 10),
        raw: trimmed,
      };
    }
  }

  const annexe = ANNEXE_RE.exec(trimmed);
  if (annexe && annexe[1]) {
    const value = annexe[1].toUpperCase();
    const asInt = parseInt(value, 10);
    return {
      type: 'annexe',
      value,
      numericValue: Number.isNaN(asInt) ? value.charCodeAt(0) - 64 : asInt,
      raw: trimmed,
    };
  }

  return { type: 'other', value: trimmed.toLowerCase(), raw: trimmed };
}

/* ────────────────────────────────────────────────────────────────────────
 * Extraction d'un numéro de pièce EN TÊTE d'un libellé/nom de fichier.
 *
 * Cas d'usage : au dépôt d'une pièce, l'avocat a souvent déjà écrit le numéro
 * dans le nom du fichier (« PIÈCE 14. Acte d'honoraires du 23 juillet »). On
 * veut alors extraire « 14 » vers `case_documents.piece_number` et ne garder
 * que la partie descriptive (« Acte d'honoraires du 23 juillet ») comme
 * libellé. `parsePieceNumber` (ci-dessus) NORMALISE un numéro déjà isolé mais
 * ne sait pas RETIRER le préfixe « Pièce n° » — c'est le rôle de ce bloc.
 *
 * Décisions (verrouillées 2026-07-07, cf. plan
 * docs/features/piece-ingestion/plans/2026-07-07-auto-extract-piece-number-on-deposit.html) :
 *  - Niveau A : marqueur « Pièce/PIÈCE/Pce » explicite → haute confiance,
 *    numéro de longueur libre (le mot lève l'ambiguïté).
 *  - Niveau B : numéro NU en tête → ≤3 chiffres + séparateur DUR (`. - _ ) :`,
 *    PAS `/`) + libellé commençant par un non-chiffre. Ces garde-fous excluent
 *    les années (« 2024 - Bilan ») et les dates (« 12/03/2024 », « 12.03.2024 »).
 *  - « Nom = numéro uniquement » AVEC marqueur (« Pièce 14 ») : on extrait le
 *    numéro ET on garde le nom d'origine (décision révisée 2026-07-08).
 *  - Niveau B′ (opt-in, 2026-09-22, #1704) : numéro NU + ESPACE, réservé aux
 *    dossiers de droit public, cf. {@link ExtractLeadingPieceNumberOptions}.
 * ──────────────────────────────────────────────────────────────────────── */

/** Résultat de {@link extractLeadingPieceNumber}. */
export interface LeadingPieceExtraction {
  /** Numéro canonique extrait (`'14'`, `'3bis'`, `'Annexe A'`) ou `null`. */
  pieceNumber: string | null;
  /** Libellé à stocker : descriptif nettoyé si `matched`, sinon le nom d'origine. */
  label: string;
  /** `true` seulement si un numéro ET un libellé descriptif ont été isolés. */
  matched: boolean;
}

/** Options de {@link extractLeadingPieceNumber}. Toutes désactivées par défaut. */
export interface ExtractLeadingPieceNumberOptions {
  /**
   * Niveau B′ : accepte une simple ESPACE après un numéro nu
   * (« 1 Carte d'identité », « 001 Arrêté préfectoral »).
   *
   * À n'activer que pour un dossier de droit public (convention
   * `administratif` de `@hector/shared-types`) : devant le TA, la CAA et le CE,
   * Télérecours exige un nom de fichier qui COMMENCE par le numéro d'ordre
   * (R. 414-5 / R. 611-8-5 CJA), et l'usage des avocats est « 1 Libellé ».
   * En civil, « 3 quittances de loyer » est une quantité, pas la pièce 3 :
   * l'option reste donc éteinte et le niveau B exige un séparateur dur.
   *
   * Même activée, l'option ne prend jamais la main sur les niveaux A et B :
   * elle n'est tentée qu'en dernier, sur un nom qu'ils ont refusé.
   */
  allowSpaceAfterBareNumber?: boolean;
}

/** Mot « Pièce » sous toutes ses formes : casse, accent è/e, pluriel, abréviation « Pce ». */
const MARKER_SRC = 'pi[eèéEÈÉ]ces?|pces?\\.?';
/** Qualificatif optionnel absorbé (« Pièce adverse », « Pièce communiquée »). */
const QUAL_SRC = '(?:adverses?|communiqu[eé]e?s?)';
/** « n° » optionnel : n° (U+00B0), nº (U+00BA ordinal), no, n, n., numéro, #. */
const NKEY_SRC = 'n[°ºo\\.]?|numero|numéro|#';
/** Suffixes juridiques FR. */
const SUFFIX_SRC = 'bis|ter|quater|quinquies|sexies|septies|octies|nonies|decies';
/** Token numéro : entier (+ suffixe FR) OU « Annexe X ». */
const NUM_SRC = `\\d+(?:\\s*(?:${SUFFIX_SRC}))?|annexes?\\s*[a-z0-9]+`;
/** N'importe quel séparateur (0..n) : . - – — _ / ) : | » > • + espaces. */
const SEP_SRC = '[\\s._\\-/):|»>–—•]';
/** Extensions bureautiques à ignorer pour l'analyse (ré-appendées au libellé). */
const DOC_EXT_RE =
  /\.(pdf|docx?|xlsx?|pptx?|eml|msg|txt|rtf|odt|ods|csv|jpe?g|png|tiff?|heic|webp)$/i;

/** Niveau A — marqueur « Pièce » explicite (permissif). */
const LEADING_WORDED_RE = new RegExp(
  `^\\s*(?:${MARKER_SRC})${SEP_SRC}*(?:${QUAL_SRC}${SEP_SRC}*)?(?:(?:${NKEY_SRC})${SEP_SRC}*)?(${NUM_SRC})(?:${SEP_SRC}+)(.+)$`,
  'iu',
);

/** Niveau A bis — « Annexe X » en tête sans le mot « Pièce » (le mot « Annexe » lève l'ambiguïté). */
const LEADING_ANNEXE_RE = new RegExp(
  `^\\s*(annexes?\\s*[a-z0-9]+)(?:${SEP_SRC}+)(.+)$`,
  'iu',
);

/**
 * Niveau B — numéro NU ≤3 chiffres + séparateur DUR + libellé non-numérique.
 * Séparateurs durs = `. - _ ) :` (volontairement PAS `/`, réservé aux dates).
 * Le lookahead `(?=[^\d\s])` garantit que le libellé commence par un
 * non-chiffre → « 12.03.2024 Bilan » n'est jamais pris pour la pièce 12.
 */
const LEADING_BARE_RE =
  /^\s*(\d{1,3}(?:\s*(?:bis|ter|quater))?)\s*[.\-_):]\s*(?=[^\d\s])(.+)$/iu;

/**
 * Niveau B′ (opt-in `allowSpaceAfterBareNumber`) — numéro NU ≤3 chiffres
 * (+ bis/ter/quater), ESPACE, puis libellé commençant par une LETTRE.
 *
 * Garde-fous structurels, sans liste :
 *  - ≤3 chiffres puis espace obligatoire : « 2024 Bilan » ne matche pas.
 *  - libellé commençant par une lettre : « 12 03 2024 Bilan » et
 *    « 3 000 euros facture » ne matchent pas (le libellé commence par un chiffre).
 *  - ordinal collé : « 1er avril relevé », « 2ème mémoire » ne matchent pas,
 *    le chiffre n'étant pas suivi d'une espace.
 *  - le suffixe bis/ter/quater doit être un mot entier (`(?!\p{L})`) :
 *    « 3 terrain » donne la pièce 3 « terrain », pas « 3ter » « rain ».
 * Les garde-fous lexicaux sont dans {@link SPACE_LABEL_REJECT_RE}.
 */
const LEADING_BARE_SPACE_RE =
  /^\s*(\d{1,3}(?:\s*(?:bis|ter|quater)(?!\p{L}))?)\s+(?=\p{L})(.+)$/iu;

/**
 * Premier mot du libellé qui trahit une date, une quantité ou une adresse et
 * non une pièce numérotée. Testé sur le libellé capturé par
 * {@link LEADING_BARE_SPACE_RE} (et non dans la regex elle-même, où le retour
 * arrière contournerait la garde : « 2 bis rue » se relirait « 2 » + « bis rue »).
 * Le mot doit être entier (`(?!\p{L})`) : « Décision », « Octroi », « Maison »,
 * « Mail », « Placement » passent. Un point d'abréviation (« déc. ») est un
 * non-lettre, il est donc couvert sans être écrit.
 */
const SPACE_LABEL_REJECT_RE = new RegExp(
  '^(?:' +
    [
      // Mois et abréviations, accent facultatif : « 12 mars 2024 courrier »,
      // « 3 sept. 2023 relevé », « 1 aout 2022 avis ».
      'janvier|janv?|f[ée]vrier|f[ée]vr?|mars|avril|avr|mai|juin|juillet|juil|ao[uû]t|septembre|sept|octobre|oct|novembre|nov|d[ée]cembre|d[ée]c',
      // Jours de la semaine : « 15 lundi réunion », « 2 lundis par mois ».
      '(?:lun|mar|mercre|jeu|vendre|same)dis?|dimanches?',
      // Ordinal séparé du chiffre par une espace : « 1 er avril », « 2 ème mémoire ».
      'er|re|[èe]re|[èe]me|e',
      // Montants, durées, surfaces : « 300 euros facture », « 15 jours de retard »,
      // « 3 mois de loyer », « 2 ans », « 50 m2 plan », « 3 ha parcelle ».
      'euros?|eur|ans?|mois|jours?|k?m[2²]|km|ha',
      // Types de voie, fréquents en urbanisme : « 12 rue des Lilas - permis »,
      // « 3 avenue Foch », « 2 bis bd Voltaire ».
      'rue|avenue|av|bd|boulevard|place|impasse|all[ée]e|chemin|route|quai|square|cours|faubourg',
    ].join('|') +
    ')(?!\\p{L})',
  'iu',
);

/**
 * Applique le niveau B′ : même forme de retour que `RegExp.exec` (numéro en
 * `[1]`, libellé en `[2]`) pour s'insérer dans la chaîne des niveaux A et B.
 */
function execSpaceSeparatedBareNumber(analysed: string): RegExpExecArray | null {
  const m = LEADING_BARE_SPACE_RE.exec(analysed);
  if (!m || SPACE_LABEL_REJECT_RE.test(m[2] ?? '')) return null;
  return m;
}

/**
 * Niveau A′ — marqueur « Pièce » EXPLICITE + numéro SEUL, sans aucun libellé
 * descriptif (« Pce 7 », « Pièce 14 », « Pièce n°14 »). Réservé au marqueur
 * worded : un numéro nu (« 7 ») ou une année (« 2024 ») ne doivent pas déclencher.
 */
const LEADING_WORDED_ONLY_RE = new RegExp(
  `^\\s*(?:${MARKER_SRC})${SEP_SRC}*(?:${QUAL_SRC}${SEP_SRC}*)?(?:(?:${NKEY_SRC})${SEP_SRC}*)?(${NUM_SRC})${SEP_SRC}*$`,
  'iu',
);

/**
 * Sérialise un {@link ParsedPiece} vers la forme canonique stockée en
 * `piece_number` (`'14'`, `'3bis'`, `'Annexe A'`). Renvoie `null` si le numéro
 * est invalide ou vaut 0 — `case_documents.piece_number` n'accepte JAMAIS
 * `'0'` (un acte ne cite jamais de « Pièce n°0 », cf. renumber-pieces).
 */
export function canonicalPieceNumber(parsed: ParsedPiece): string | null {
  if (parsed.type === 'numeric') {
    if (parsed.base === undefined || parsed.base <= 0) return null;
    const suffix = parsed.suffix ?? '';
    const sub = parsed.sub && parsed.sub > 0 ? `.${parsed.sub}` : '';
    return `${parsed.base}${suffix}${sub}`;
  }
  if (parsed.type === 'roman') {
    // Séparateur canonique `-` : le dossier s'écrit « I - ETAT CIVIL » (espacé)
    // mais la pièce se stocke « I-1 », compact (constaté en prod 2026-07-08).
    if (!parsed.value || !parsed.sub || parsed.sub <= 0) return null;
    return `${parsed.value}-${parsed.sub}`;
  }
  if (parsed.type === 'annexe' && parsed.value) {
    return `Annexe ${parsed.value}`;
  }
  return null;
}

/**
 * Détecte + extrait un numéro de pièce présent EN TÊTE d'un nom de fichier /
 * libellé, et renvoie le libellé descriptif nettoyé du préfixe.
 *
 * L'extension bureautique éventuelle est retirée pour l'analyse puis
 * ré-appendée au libellé (comportement d'extension inchangé vs aujourd'hui).
 *
 * @param rawName Nom du fichier déposé (ou libellé).
 * @param options Voir {@link ExtractLeadingPieceNumberOptions} ; sans option,
 *   le comportement est celui des niveaux A et B seuls.
 *
 * @example
 * extractLeadingPieceNumber("PIÈCE 14. Acte d'honoraires")
 * // { pieceNumber: '14', label: "Acte d'honoraires", matched: true }
 * extractLeadingPieceNumber('Pièce 14')       // { pieceNumber: '14', label: 'Pièce 14', matched: true }
 * extractLeadingPieceNumber('2024 - Bilan')   // { pieceNumber: null, label: '2024 - Bilan', matched: false }
 * extractLeadingPieceNumber('Contrat de prêt')// { pieceNumber: null, label: 'Contrat de prêt', matched: false }
 * extractLeadingPieceNumber("1 Carte d'identité.pdf")
 * // { pieceNumber: null, label: "1 Carte d'identité.pdf", matched: false }
 * extractLeadingPieceNumber("1 Carte d'identité.pdf", { allowSpaceAfterBareNumber: true })
 * // { pieceNumber: '1', label: "Carte d'identité.pdf", matched: true }
 */
export function extractLeadingPieceNumber(
  rawName: unknown,
  options: ExtractLeadingPieceNumberOptions = {},
): LeadingPieceExtraction {
  if (typeof rawName !== 'string') {
    return { pieceNumber: null, label: String(rawName ?? ''), matched: false };
  }
  // macOS (Finder / APFS) livre les noms de fichiers en Unicode **NFD**
  // (décomposé) : « è » = « e » + U+0300 (grave combinant). Nos regexes de
  // marqueur sont écrites avec des littéraux **NFC** (précomposés, `[eèéEÈÉ]`).
  // Sans normalisation, un « Pièce »/« PIÈCE » déposé depuis un Mac ne matche
  // PAS (le combining mark s'intercale et casse la classe) → seuls les marqueurs
  // sans accent (« PIECE ») passaient. On normalise donc en NFC avant toute
  // analyse ; le libellé extrait est renvoyé en NFC (forme canonique, meilleure
  // pour l'affichage et la recherche). Bug révélé en dogfooding 2026-07-08.
  const trimmed = rawName.normalize('NFC').trim();
  const ext = trimmed.match(DOC_EXT_RE)?.[0] ?? '';
  // Analyse sur le nom sans extension ; le libellé final la ré-appende.
  const analysed = ext ? trimmed.slice(0, trimmed.length - ext.length) : trimmed;

  // 1. Marqueur/numéro SUIVI d'un libellé descriptif → extraire le numéro et ne
  //    garder que le libellé nettoyé (« PIÈCE 14. Acte » → 14 + « Acte »).
  //    Le niveau B′ passe en dernier : option éteinte, la chaîne est celle d'avant.
  const m =
    LEADING_WORDED_RE.exec(analysed) ??
    LEADING_ANNEXE_RE.exec(analysed) ??
    LEADING_BARE_RE.exec(analysed) ??
    (options.allowSpaceAfterBareNumber ? execSpaceSeparatedBareNumber(analysed) : null);
  if (m) {
    const labelCore = (m[2] ?? '').trim();
    const pieceNumber = canonicalPieceNumber(parsePieceNumber(m[1]));
    if (labelCore !== '' && pieceNumber) {
      return { pieceNumber, label: `${labelCore}${ext}`, matched: true };
    }
  }

  // 2. Marqueur EXPLICITE + numéro SEUL, aucun libellé descriptif (« Pce 7 »,
  //    « Pièce 14 », « Pièce n°14 ») : il n'y a rien d'autre à isoler → on extrait
  //    quand même le numéro ET on GARDE le nom d'origine (normalisé NFC). Décision
  //    révisée 2026-07-08 (remplace « décision A » : l'avocat veut la pièce
  //    numérotée même quand son nom se réduit au numéro).
  const mOnly = LEADING_WORDED_ONLY_RE.exec(analysed);
  if (mOnly) {
    const pieceNumber = canonicalPieceNumber(parsePieceNumber(mOnly[1]));
    if (pieceNumber) return { pieceNumber, label: trimmed, matched: true };
  }

  return { pieceNumber: null, label: rawName, matched: false };
}

/* ────────────────────────────────────────────────────────────────────────
 * Reflow d'une numérotation en suivant le GABARIT de la pièce du haut (l'ancre).
 *
 * Cas d'usage : dans « Numéroter mes pièces » et « Tamponner mes pièces », quand
 * l'avocat déplace une pièce par glisser-déposer, la numérotation doit se
 * réorganiser en suivant la « norme » de la numérotation en place — et c'est la
 * PIÈCE DU HAUT qui définit cette norme, celle d'AVANT le déplacement
 * (`reflowAfterReorder`). Déplacer la pièce 8 en position 2 → elle
 * devient 2, les pièces en dessous glissent (`1..8`). Si la numérotation est
 * `1.2, 1.3…` on reste dans la famille `1.x` ; idem pour `I-1`, `I.2`, `Annexe A`.
 *
 * Décision produit (2026-07-14, remplace la sémantique « glisser = réordonner
 * sans renuméroter » du 2026-07-08 pour le drag) :
 *  - Modèle A « on continue depuis le haut » : l'ancre GARDE son numéro et fixe le
 *    départ. Top `1` → 1,2,3 ; top `3` → 3,4,5 ; top `1.2` → 1.2,1.3,1.4. C'est le
 *    moins destructif (une liste qui démarre volontairement à 3, ou une famille
 *    `1.x`, reste cohérente).
 *  - L'ancien reflow retiré en 2026-07-08 était bête (`1..N` à plat) et écrasait
 *    les formats exotiques ; celui-ci est **format-aware** — il reproduit le
 *    gabarit au lieu de l'aplatir.
 * ──────────────────────────────────────────────────────────────────────── */

/** Gabarit de numérotation inféré depuis une pièce « ancre ». */
interface NumberingScheme {
  /** Produit le numéro au décalage `offset` (0 = position de l'ancre). */
  render: (offset: number) => string;
  /**
   * `true` si l'ancre porte une norme exploitable (compteur numérique, série de
   * lettres/annexe) → sa position 0 est conservée verbatim. `false` pour le repli
   * arabe à plat (ancre vide ou texte sans compteur) : là, la position 0 est
   * renumérotée `1` comme les autres — rien à préserver.
   */
  anchored: boolean;
}

/** Entier ≥1 → lettres base-26 bijective : 1→'A', 26→'Z', 27→'AA'. */
function intToLetters(oneBased: number): string {
  let n = Math.max(1, oneBased);
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/** Lettres → entier ≥1 (inverse de {@link intToLetters}) : 'A'→1, 'AA'→27. */
function lettersToInt(letters: string): number {
  let n = 0;
  for (const ch of letters.toUpperCase()) {
    n = n * 26 + (ch.charCodeAt(0) - 64);
  }
  return n;
}

/**
 * Infère le gabarit d'incrémentation depuis une ancre.
 *
 * Règle générale « compteur en queue » : le DERNIER groupe de chiffres est le
 * compteur, tout ce qui le précède est un préfixe littéral opaque (`1.`, `1-`,
 * `I-`, `I.`, `Annexe `…) et tout ce qui le suit un suffixe littéral (`)`, `.`…).
 * Aucune arithmétique romaine nécessaire — `I-`, `II-` sont de simples préfixes.
 */
function inferNumberingScheme(anchor: string): NumberingScheme {
  const s = anchor.trim();

  // 1. Ancre vide → arabe à plat depuis 1 (aucune norme à suivre).
  if (s === '') return { render: (k) => String(1 + k), anchored: false };

  // 2. « Annexe A » (annexe à LETTRE) → série de lettres. Testé avant la règle
  //    « compteur en queue » car il n'y a pas de chiffre à capter ici.
  const parsed = parsePieceNumber(s);
  if (parsed.type === 'annexe' && parsed.value && /^[A-Za-z]+$/.test(parsed.value)) {
    const m = /^(.*?)[A-Za-z]+\s*$/.exec(s); // préfixe littéral exact (casse conservée)
    const prefix = m?.[1] ?? 'Annexe ';
    const start = lettersToInt(parsed.value);
    return { render: (k) => `${prefix}${intToLetters(start + k)}`, anchored: true };
  }

  // 3. Compteur numérique EN QUEUE (règle générale) — couvre `1`, `1.2`, `1-2`,
  //    `I-1`, `I.2`, `II-3`, `Annexe 1`, `2)`, `2.`, `01`, `1.2.3`…
  const digits = /^(.*?)(\d+)(\D*)$/.exec(s);
  if (digits) {
    const prefix = digits[1] ?? '';
    const counter = digits[2] ?? '0';
    const suffix = digits[3] ?? '';
    const start = parseInt(counter, 10);
    // Conserve les zéros de tête (`01` → `02`), sinon pas de padding.
    const pad = counter.startsWith('0') ? counter.length : 0;
    return {
      render: (k) => `${prefix}${String(start + k).padStart(pad, '0')}${suffix}`,
      anchored: true,
    };
  }

  // 4. Lettre ISOLÉE (seul alphanumérique = 1 lettre : `A`, `A)`, `(A)`) → A,B,C.
  const single = /^([^0-9A-Za-z]*)([A-Za-z])([^0-9A-Za-z]*)$/.exec(s);
  if (single) {
    const pre = single[1] ?? '';
    const start = lettersToInt(single[2] ?? 'A');
    const post = single[3] ?? '';
    return { render: (k) => `${pre}${intToLetters(start + k)}${post}`, anchored: true };
  }

  // 5. Texte sans chiffre ni lettre isolée → repli arabe à plat depuis 1.
  return { render: (k) => String(1 + k), anchored: false };
}

/**
 * Reflow une séquence de numéros en suivant le gabarit de la pièce du haut.
 *
 * Retourne `count` numéros successifs. L'élément 0 vaut l'ancre **verbatim**
 * (la pièce du haut ne change jamais de numéro) ; les suivants incrémentent le
 * dernier segment numérique du gabarit (Modèle A — on continue depuis le haut).
 *
 * @param anchor Numéro de la pièce du haut (celle qui définit la norme). Vide/null
 *   ⇒ repli arabe à plat `1..count`.
 * @param count  Nombre de pièces à numéroter (≤0 ⇒ `[]`).
 *
 * @example
 * reflowFromAnchor('1', 4)        // ['1','2','3','4']
 * reflowFromAnchor('1.2', 3)      // ['1.2','1.3','1.4']
 * reflowFromAnchor('I-1', 3)      // ['I-1','I-2','I-3']
 * reflowFromAnchor('Annexe A', 3) // ['Annexe A','Annexe B','Annexe C']
 * reflowFromAnchor('', 3)         // ['1','2','3']
 */
export function reflowFromAnchor(
  anchor: string | null | undefined,
  count: number,
): string[] {
  if (!Number.isFinite(count) || count <= 0) return [];
  const trimmed = typeof anchor === 'string' ? anchor.trim() : '';
  const scheme = inferNumberingScheme(trimmed);
  const out: string[] = [];
  for (let k = 0; k < count; k++) {
    // Position 0 = l'ancre verbatim UNIQUEMENT si elle porte une norme (scheme
    // `anchored`) : quel que soit son format exotique ou sa casse, la pièce du
    // haut garde exactement son numéro. Un repli à plat (ancre vide ou texte sans
    // compteur) renumérote aussi la position 0 → `1`, rien à préserver.
    out.push(k === 0 && scheme.anchored ? trimmed : scheme.render(k));
  }
  return out;
}

/**
 * Renumérote une liste après un réordonnancement (glisser-déposer, tri).
 *
 * La norme vient de la tête de liste AVANT le déplacement, pas de la pièce qui
 * arrive en haut : c'est la POSITION qui porte le départ de la numérotation.
 * Ancrer sur la pièce arrivée en tête (comportement jusqu'au 2026-10-08) faisait
 * qu'une pièce n° 7 glissée en première position gardait 7 et décalait toute la
 * liste en 7, 8, 9… au lieu de devenir la pièce n° 1.
 *
 * @param previousOrder Ids numérotables dans l'ordre d'avant le déplacement.
 * @param nextOrder     Ids numérotables dans le nouvel ordre.
 * @param numberOf      Numéro actuel d'une pièce (avant renumérotation).
 * @returns Les nouveaux numéros, alignés sur `nextOrder`.
 *
 * @example
 * // 1,2,3,4 — la pièce 4 glissée en tête devient 1
 * reflowAfterReorder(['a','b','c','d'], ['d','a','b','c'], numberOf) // ['1','2','3','4']
 */
export function reflowAfterReorder(
  previousOrder: readonly string[],
  nextOrder: readonly string[],
  numberOf: (id: string) => string | null | undefined,
): string[] {
  const head = previousOrder[0];
  return reflowFromAnchor(head === undefined ? '' : numberOf(head), nextOrder.length);
}

/** Compare deux pièces déjà parsées. Négatif si a < b. */
export function compareParsedPieces(a: ParsedPiece, b: ParsedPiece): number {
  const typeCompare = TYPE_ORDER[a.type] - TYPE_ORDER[b.type];
  if (typeCompare !== 0) return typeCompare;

  if (a.type === 'numeric' && b.type === 'numeric') {
    if (a.base !== b.base) return (a.base ?? 0) - (b.base ?? 0);
    const suffixA = SUFFIX_ORDER[a.suffix ?? ''] ?? 99;
    const suffixB = SUFFIX_ORDER[b.suffix ?? ''] ?? 99;
    if (suffixA !== suffixB) return suffixA - suffixB;
    return (a.sub ?? 0) - (b.sub ?? 0);
  }

  if (a.type === 'roman' && b.type === 'roman') {
    if (a.romanValue !== b.romanValue) {
      return (a.romanValue ?? 0) - (b.romanValue ?? 0);
    }
    return (a.sub ?? 0) - (b.sub ?? 0);
  }

  if (a.type === 'annexe' && b.type === 'annexe') {
    return (a.numericValue ?? 0) - (b.numericValue ?? 0);
  }

  return String(a.value ?? '').localeCompare(String(b.value ?? ''), 'fr', {
    numeric: true,
  });
}

/**
 * Compare deux numéros de pièces (chaînes brutes) — comparateur pour
 * `Array.prototype.sort`.
 *
 * @example ['3','1','2','1bis'].sort(comparePieceNumbers) // ['1','1bis','2','3']
 */
export function comparePieceNumbers(a: unknown, b: unknown): number {
  return compareParsedPieces(parsePieceNumber(a), parsePieceNumber(b));
}

/**
 * Fabrique un comparateur pour des objets portant un champ numéro de pièce.
 *
 * @param property Nom du champ contenant le numéro (défaut `piece_number`).
 */
export function createPieceComparator<T>(
  property = 'piece_number',
): (a: T, b: T) => number {
  return (a, b) =>
    comparePieceNumbers(
      (a as Record<string, unknown> | null)?.[property],
      (b as Record<string, unknown> | null)?.[property],
    );
}

/**
 * Trie (copie, ne mute pas) un tableau de pièces par leur numéro.
 *
 * @param property Nom du champ contenant le numéro (défaut `piece_number`).
 */
export function sortPieces<T>(pieces: T[], property = 'piece_number'): T[] {
  if (!Array.isArray(pieces)) return [];
  return [...pieces].sort(createPieceComparator<T>(property));
}

export { SUFFIX_ORDER, TYPE_ORDER };

/**
 * Sections romaines (sous-dossiers `I - …`, `II - …`) — cf. `romanSections.ts`
 * pour le raisonnement sur l'alphabet `[IVX]` et l'exclusion de `L C D M`.
 */
export { romanToInt, parseFolderSection, detectRomanSectioning } from './romanSections';
export type { FolderSection, RomanSectioning } from './romanSections';

/** Shared source numbering for manual and agent-driven PDF splitting. */
export { parsePieceNumberSeed, parseNameRange, detectSplitNumbering, formatPieceNumber } from './splitNumbering';
export type { PieceNumberSeed, SplitNumberingOrigin, SplitNumberingDetection } from './splitNumbering';
